import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  EyeIcon,
  BanknotesIcon,
  CursorArrowRaysIcon,
  TagIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  ClockIcon,
  ShieldCheckIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { useCampaign } from '../hooks/useCampaigns';
import type { CampaignStatus, ImpressionLimit, ImpressionLimitType } from '../types/campaign';
import { campaignApi } from '../services/api';

const CampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { campaign, loading, error, fetchCampaign } = useCampaign(id!);
  
  const [activeTab, setActiveTab] = useState<'overview' | 'targeting' | 'performance'>('overview');
  const [processingKeywords, setProcessingKeywords] = useState(false);
  const [hoveredKeyword, setHoveredKeyword] = useState<string | null>(null);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [updatingCreative, setUpdatingCreative] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchCampaign();
    }
  }, [id, fetchCampaign]);

  const handleProcessKeywords = async () => {
    if (!id || processingKeywords) return;

    setProcessingKeywords(true);
    try {
      const result = await campaignApi.processKeywords(id);
      
      // Show success message
      console.log('Keyword processing result:', result);
      alert(`Keyword processing completed! Found ${result.data?.result?.totalMatches || 0} matches across ${result.data?.result?.contentMatched || 0} content items.`);
      
      // Refresh campaign data to get updated keyword match counts
      await fetchCampaign();
      
      // Optionally refetch campaign data to update UI
      await fetchCampaign();
    } catch (error) {
      console.error('Keyword processing error:', error);
      alert('An error occurred while processing keywords. Please try again.');
    } finally {
      setProcessingKeywords(false);
    }
  };

  const getStatusColor = (status: CampaignStatus): string => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'draft':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: CampaignStatus): string => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'paused':
        return 'Duraklatıldı';
      case 'completed':
        return 'Tamamlandı';
      case 'draft':
        return 'Taslak';
      default:
        return status;
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDisplayTypeText = (displayType: string): string => {
    const types: Record<string, string> = {
      'banner': 'Banner',
      'video_overlay': 'Video Overlay',
      'third_party': 'Üçüncü Taraf',
      'l_banner': 'L Banner',
      'square_banner': 'Kare Banner',
      'bottom_banner': 'Alt Banner',
      'full_page': 'Tam Sayfa'
    };
    return types[displayType] || displayType;
  };

  const getLimitTypeText = (limitType: ImpressionLimitType): string => {
    const types: Record<ImpressionLimitType, string> = {
      'daily_campaign': 'Günlük Kampanya Limiti',
      'total_campaign': 'Toplam Kampanya Limiti',
      'daily_user': 'Günlük Kullanıcı Limiti',
      'weekly_user': 'Haftalık Kullanıcı Limiti'
    };
    return types[limitType] || limitType;
  };

  const formatLimitProgress = (current: number, limit: number): string => {
    const percentage = limit > 0 ? Math.round((current / limit) * 100) : 0;
    return `${current.toLocaleString('tr-TR')} / ${limit.toLocaleString('tr-TR')} (${percentage}%)`;
  };

  const getLimitProgressColor = (current: number, limit: number): string => {
    const percentage = limit > 0 ? (current / limit) * 100 : 0;
    if (percentage >= 90) return 'text-red-600 bg-red-50';
    if (percentage >= 75) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const formatSeconds = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleKeywordHover = (keyword: string, event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPopoverPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 10
    });
    setHoveredKeyword(keyword);
  };

  const handleKeywordLeave = () => {
    setTimeout(() => {
      setHoveredKeyword(null);
    }, 150); // Small delay to allow moving to popover
  };

  const handlePopoverEnter = () => {
    // Keep popover open when hovering over it
  };

  const handlePopoverLeave = () => {
    setHoveredKeyword(null);
  };

  const handleToggleCreativeStatus = async (creativeId: string, currentStatus: string) => {
    if (!id || updatingCreative) return;

    setUpdatingCreative(creativeId);
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      await campaignApi.toggleCreativeStatus(id, creativeId, newStatus);
      
      // Refresh campaign data to get updated creative status
      await fetchCampaign();
      
      console.log(`Creative ${creativeId} status updated to ${newStatus}`);
    } catch (error) {
      console.error('Creative status update error:', error);
      alert('Creative durumu güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setUpdatingCreative(null);
    }
  };

  if (loading && !campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Hata</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h3 className="mt-2 text-sm font-medium text-gray-900">Kampanya Bulunamadı</h3>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Genel Bakış', icon: EyeIcon },
    { id: 'targeting', name: 'Hedefleme', icon: CursorArrowRaysIcon },
    { id: 'performance', name: 'Performans', icon: ChartBarIcon },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/campaigns')}
              className="mr-4 p-2 rounded-md text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <div className="flex items-center">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-300">{campaign.name}</h1>
                <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(campaign.status || 'draft')}`}>
                  {getStatusText(campaign.status || 'draft')}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500">{campaign.description}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(`/campaigns/${id}/edit`)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Düzenle
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Basic Info */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Temel Bilgiler</h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Kampanya Tipi</dt>
                  <dd className="mt-1 text-sm text-gray-900 capitalize">{campaign.campaignType}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Para Birimi</dt>
                  <dd className="mt-1 text-sm text-gray-900">{campaign.currency || 'USD'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Bütçe</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {campaign.budget ? formatCurrency(campaign.budget) : 'Belirlenmemiş'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Durum</dt>
                  <dd className="mt-1 text-sm text-gray-900">{getStatusText(campaign.status || 'draft')}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Başlangıç Tarihi</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {campaign.startDate ? formatDate(campaign.startDate) : 'Belirlenmemiş'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Bitiş Tarihi</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {campaign.endDate ? formatDate(campaign.endDate) : 'Belirlenmemiş'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Campaign Limits */}
          {(campaign as any).impressionLimits && (campaign as any).impressionLimits.length > 0 && (
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 flex items-center">
                  <ShieldCheckIcon className="h-5 w-5 mr-2" />
                  Kampanya Limitleri
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {((campaign as any).impressionLimits as ImpressionLimit[]).map((limit: ImpressionLimit) => (
                    <div key={limit.id || limit.limitType} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900">
                          {getLimitTypeText(limit.limitType)}
                        </span>
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">Kullanım:</span>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            getLimitProgressColor(limit.currentCount || 0, limit.limitValue)
                          }`}>
                            {formatLimitProgress(limit.currentCount || 0, limit.limitValue)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              (limit.currentCount || 0) / limit.limitValue >= 0.9 ? 'bg-red-500' :
                              (limit.currentCount || 0) / limit.limitValue >= 0.75 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ 
                              width: `${Math.min(((limit.currentCount || 0) / limit.limitValue) * 100, 100)}%` 
                            }}
                          ></div>
                        </div>
                        {limit.resetAt && (
                          <div className="text-xs text-gray-500">
                            Sıfırlanma: {formatDate(limit.resetAt)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {((campaign as any).impressionLimits as ImpressionLimit[]).length === 0 && (
                  <div className="text-center py-4">
                    <ShieldCheckIcon className="mx-auto h-8 w-8 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">Bu kampanya için limit belirlenmemiş</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <EyeIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Görüntülenme</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {(campaign as any).metrics?.impressions || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CursorArrowRaysIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Tıklama</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {(campaign as any).metrics?.clicks || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <BanknotesIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Harcama</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {(campaign as any).metrics?.spend ? formatCurrency((campaign as any).metrics.spend) : '₺0'}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChartBarIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">CTR</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {(campaign as any).metrics?.ctr ? `${(campaign as any).metrics.ctr.toFixed(2)}%` : '0%'}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ad Creatives Section */}
          {campaign.adCreatives && campaign.adCreatives.length > 0 && (
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 flex items-center">
                  <PhotoIcon className="h-5 w-5 mr-2" />
                  Reklam Kreatifleri ({campaign.adCreatives.length})
                </h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {campaign.adCreatives.map((creative: any) => (
                    <div key={creative.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="aspect-w-16 aspect-h-9 mb-3">
                        {creative.imageUrl ? (
                          <img
                            src={creative.imageUrl}
                            alt={creative.title}
                            className="w-full h-32 object-cover rounded-md"
                          />
                        ) : (
                          <div className="w-full h-32 bg-gray-100 rounded-md flex items-center justify-center">
                            <PhotoIcon className="h-8 w-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{creative.title}</h4>
                        <button
                          onClick={() => handleToggleCreativeStatus(creative.id, creative.status)}
                          disabled={updatingCreative === creative.id}
                          className={`inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded-md transition-colors ${
                            updatingCreative === creative.id
                              ? 'border-gray-300 text-gray-500 bg-gray-100 cursor-not-allowed'
                              : creative.status === 'active'
                              ? 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100'
                              : 'border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-100'
                          }`}
                        >
                          {updatingCreative === creative.id ? 'Güncelleniyor...' : creative.status === 'active' ? 'Aktif' : 'Pasif'}
                        </button>
                      </div>
                      
                      <p className="text-sm text-gray-500 mb-3">{creative.description}</p>
                      
                      <div className="space-y-2 text-xs text-gray-600">
                        <div className="flex justify-between">
                          <span>Tip:</span>
                          <span className="font-medium">{getDisplayTypeText(creative.displayType)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Ad Tipi:</span>
                          <span className="font-medium capitalize">{creative.adType}</span>
                        </div>
                        {creative.creativeData?.price && (
                          <div className="flex justify-between">
                            <span>Fiyat:</span>
                            <span className="font-medium text-green-600">{creative.creativeData.price}</span>
                          </div>
                        )}
                      </div>

                      {creative.clickUrl && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="text-xs text-gray-500 mb-1">Click URL:</div>
                          <a
                            href={creative.clickUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 truncate block"
                          >
                            {creative.clickUrl}
                          </a>
                        </div>
                      )}

                      {creative.adServerTag && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="text-xs text-gray-500 mb-1">Ad Server Tag:</div>
                          <pre className="text-xs bg-gray-50 p-2 rounded border overflow-x-auto">
                            {creative.adServerTag}
                          </pre>
                        </div>
                      )}

                      {creative.trackingPixels && creative.trackingPixels.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="text-xs text-gray-500 mb-2">
                            Tracking Pixels ({creative.trackingPixels.length}):
                          </div>
                          <div className="space-y-2">
                            {creative.trackingPixels.map((pixel: string, index: number) => (
                              <div key={index} className="text-xs">
                                <div className="text-gray-400 mb-1">#{index + 1}:</div>
                                <div className="bg-gray-50 p-2 rounded border break-all">
                                  {pixel}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Target Keywords Section */}
          {campaign.targetKeywords && campaign.targetKeywords.length > 0 && (
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                    <TagIcon className="h-5 w-5 mr-2" />
                    Hedef Kelimeler ({campaign.targetKeywords.length})
                  </h3>
                  <button
                    onClick={handleProcessKeywords}
                    disabled={processingKeywords}
                    className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingKeywords ? (
                      <>
                        <div className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600">
                          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                        İşleniyor...
                      </>
                    ) : (
                      <>
                        <CogIcon className="h-4 w-4 mr-2" />
                        Kelimeleri İşle
                      </>
                    )}
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {campaign.targetKeywords
                    .sort((a: any, b: any) => (b.matchCount || 0) - (a.matchCount || 0))
                    .map((keyword: any) => (
                    <div key={keyword.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{keyword.keyword}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          keyword.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {keyword.status === 'active' ? 'Aktif' : 'Pasif'}
                        </span>
                      </div>
                      <div className="space-y-1 text-xs text-gray-600">
                        <div className="flex justify-between">
                          <span>Eşleşme Tipi:</span>
                          <span className="font-medium capitalize">{keyword.matchType}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Öncelik:</span>
                          <span className="font-medium">{keyword.priority}</span>
                        </div>
                        {keyword.bidAmount && (
                          <div className="flex justify-between">
                            <span>Teklif:</span>
                            <span className="font-medium">₺{keyword.bidAmount}</span>
                          </div>
                        )}
                        {keyword.matchCount !== undefined && (
                          <div className="flex justify-between">
                            <span>Eşleşme:</span>
                            <span 
                              className={`font-medium cursor-pointer ${
                                keyword.matchCount > 0 ? 'text-green-600 hover:text-green-800' : 'text-gray-500'
                              }`}
                              onMouseEnter={keyword.matchCount > 0 ? (e) => handleKeywordHover(keyword.keyword, e) : undefined}
                              onMouseLeave={keyword.matchCount > 0 ? handleKeywordLeave : undefined}
                            >
                              {keyword.matchCount > 0 ? `${keyword.matchCount} bulunan` : 'Bulunamadı'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'targeting' && (
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Hedefleme Ayarları</h3>
            
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Diğer Hedefleme Ayarları</h4>
              <pre className="text-xs text-gray-600 bg-gray-50 p-3 rounded-md overflow-auto">
                {JSON.stringify(campaign.targetingSettings || {}, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'performance' && (
        <div className="space-y-8">
          {/* Performance Chart */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 flex items-center">
                <ChartBarIcon className="h-5 w-5 mr-2" />
                Performans Analizi
              </h3>
              <div className="text-center py-8 text-gray-500">
                Performans grafiği yakında eklenecek...
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Keyword Details Popover */}
      {hoveredKeyword && (
        <div 
          className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-xs"
          style={{
            left: popoverPosition.x,
            top: popoverPosition.y,
            transform: 'translateX(-50%)'
          }}
          onMouseEnter={handlePopoverEnter}
          onMouseLeave={handlePopoverLeave}
        >
          <div className="mb-2">
            <h4 className="font-medium text-gray-900">"{hoveredKeyword}" Eşleşmeleri</h4>
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {campaign.targetKeywords
              ?.find(k => k.keyword === hoveredKeyword)
              ?.matchDetails?.map((detail, index) => (
                <div key={index} className="text-sm border-b border-gray-100 pb-2 last:border-b-0">
                  <div className="font-medium text-gray-800 truncate" title={detail.contentTitle}>
                    {detail.contentTitle}
                  </div>
                  <div className="text-gray-600 text-xs">
                    {formatSeconds(detail.startTime)} - {formatSeconds(detail.endTime)}
                  </div>
                  <div className="text-gray-500 text-xs">
                    Güven: {(detail.confidence * 100).toFixed(1)}%
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignDetail; 